import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

// prime react component
import { Toolbar } from "primereact/toolbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

// data table option
import {
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";

// farm table column
import { FarmListColumns } from "../../appconfig/DataTableColumns";

// api services
import { getAllDetails } from "../../service/ProductService";

// api constant
import { GET_FARM_LIST } from "../../config/ApiConstant";
import { globalFieldTemplate } from "../../appconfig/TemplateSetting";

const FarmList = () => {
  document.title = "Farm List | Agriown";

  // get farmer id from url
  const { id }: any = useParams();
  const farmer_id = atob(id);

  // /Set Toast/ Filter Properties
  const toast = useRef<Toast>(null);
  const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
  const [globalFilter, setGlobalFilter] = useState<any>(null);

  //Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);

  // state to store farm list
  const [farmList, setFarmList] = useState<any>([]);

  // get farm list on page load
  useEffect(() => {
    getFarmList();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [farmer_id]);

  // left toolbar
  const leftToolbarTemplate = () => {
    return (
      <span className="mr-2 md:mr-3 search-bar">
        <i className="pi pi-search" />
        <InputText
          type="search"
          className="search-input"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    );
  };

  //Clear Filters
  const rightToolbarTemplate = () => {
    return (
      <>
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="md:mr-2 p-button-outlined p-button-primary"
        ></Button>
      </>
    );
  };

  //Clear All Filter
  const clearAll = () => {
    setGlobalFieldFilter(null);
    setGlobalFilter("");
  };

  // crop template
  const cropTemplate = (rowData: any) => {
    return (
      <span>
        {!window.cn(rowData?.crop_id)
          ? rowData?.crop_id.map((crop: any) => crop.name).join(", ")
          : "-"}
      </span>
    );
  };

  // api call for get farm list by using id
  const getFarmList = async () => {
    try {
      const response = await getAllDetails(
        `${GET_FARM_LIST}?user_id=${farmer_id}`
      );

      if (response) {
        setFarmList(response);
        setPageLoad(true);
      } else {
        setFarmList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {pageLoad ? (
              <DataTable
                value={farmList}
                showGridlines
                stripedRows
                globalFilter={globalFilter || null}
                rows={defaultRowOptions}
                paginator={farmList.length >= 10}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatable-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries}
                filters={globalFieldFilter}
                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                emptyMessage="No Data Found"
                removableSort
              >
                {FarmListColumns.map((col, i) => {
                  if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "address") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        body={globalFieldTemplate}
                        style={{ maxWidth: "200px", textWrap: "inherit" }}
                        header={col.header}
                      />
                    );
                  } else if (col.field === "crop_id") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        body={cropTemplate}
                        header={col.header}
                      />
                    );
                  } else {
                    return (
                      <Column
                        key={col.field}
                        body={globalFieldTemplate}
                        sortable
                        field={col.field}
                        header={col.header}
                      />
                    );
                  }
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {FarmListColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FarmList;
